// Locale-aware date utilities
// Auto-detects browser locale via navigator.language with en-IN fallback
import { format } from 'date-fns';

export function getBrowserLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return 'en-IN';
}

function isValidDate(d: Date) {
  return d instanceof Date && !isNaN(d.getTime());
}

/** Normalize API ISO strings so timezone-less values are parsed as UTC. */
export function normalizeApiDateTimeString(dateInput?: string | null): string | null {
  if (!dateInput) return null;
  let resolved = dateInput.includes(' ') && !dateInput.includes('T')
    ? dateInput.replace(' ', 'T').trim()
    : dateInput.trim();
  if (!resolved.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(resolved)) {
    resolved += 'Z';
  }
  return resolved;
}

/**
 * Parse API date/time values (ISO strings, Java LocalDateTime arrays, Date) as UTC
 * so callers can format in the browser's local timezone.
 */
export function parseApiDateTime(
  dateInput?: string | number | Date | number[] | null
): Date | null {
  if (dateInput === null || dateInput === undefined || dateInput === '') return null;
  if (dateInput instanceof Date) return isValidDate(dateInput) ? dateInput : null;

  if (Array.isArray(dateInput)) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = dateInput;
    if (!year || !month || !day) return null;
    const ms = typeof nano === 'number' && nano > 999 ? Math.floor(nano / 1_000_000) : 0;
    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second, ms));
    return isValidDate(date) ? date : null;
  }

  if (typeof dateInput === 'string') {
    const normalized = normalizeApiDateTimeString(dateInput);
    if (!normalized) return null;
    const date = new Date(normalized);
    return isValidDate(date) ? date : null;
  }

  const date = new Date(dateInput as number);
  return isValidDate(date) ? date : null;
}

export function formatToLocaleDate(
  dateInput?: string | number | Date | null,
  locale?: string,
  options?: Intl.DateTimeFormatOptions
): string | null {
  if (!dateInput) return null;
  const resolvedLocale = locale || getBrowserLocale();
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput as any);
  if (!isValidDate(date)) return null;

  const fmtOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    ...(options || {}),
  };

  // toLocaleString may insert a comma between date and time in some locales; normalize to single space
  return date.toLocaleString(resolvedLocale, fmtOptions).replace(/,\s*/g, ' ');
}

export function formatToLocaleDateShort(dateInput?: string | number | Date | null, locale?: string): string | null {
  if (!dateInput) return null;
  const resolvedLocale = locale || getBrowserLocale();
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput as any);
  if (!isValidDate(date)) return null;
  return date.toLocaleDateString(resolvedLocale);
}

export function formatToUIDate(dateInput?: string | number | Date | null): string {
  if (!dateInput) return 'N/A';

  if (typeof dateInput === 'string') {
    // Handle dd/MM/yyyy [HH:mm...] format which New Date() might fail on depending on locale
    const ukDateMatch = dateInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (ukDateMatch) {
      const day = parseInt(ukDateMatch[1], 10);
      const month = parseInt(ukDateMatch[2], 10) - 1; // 0-indexed
      const year = parseInt(ukDateMatch[3], 10);
      const d = new Date(year, month, day);
      if (isValidDate(d)) return format(d, 'dd/MM/yyyy');
    }
  }

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput as any);
  if (!isValidDate(date)) return 'N/A';

  return format(date, 'dd/MM/yyyy');
}
export function formatToUIDateTime(dateInput?: string | number | Date | null): string {
  if (!dateInput) return 'N/A';

  const date = parseApiDateTime(dateInput);
  if (!date) return 'N/A';

  // Uses browser locale + its timezone automatically:
  return date.toLocaleString(getBrowserLocale(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).replace(/,\s*/g, ' ');
}

export function formatToUIDateTimeLong(dateInput?: string | number | Date | null): string {
  if (!dateInput) return '—';

  const date = parseApiDateTime(dateInput);
  if (!date) return '—';

  return format(date, 'dd/MM/yyyy hh:mm a');
}

/**
 * Formats a time string (e.g. "14:30:00" or "00:15") to a 12-hour UI format (e.g. "2:30 PM" or "12:15 AM")
 */
export function formatToUITime(timeStr?: string | null): string {
  if (!timeStr) return '';

  try {
    // If it's just a time string like "HH:mm:ss" or "HH:mm", we need to give it a date to parse it
    const [hours, minutes] = timeStr.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return timeStr;

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    return format(date, 'h:mm a');
  } catch (e) {
    return timeStr;
  }
}

// Transform single object's createdAt/updatedAt to locale formatted strings
export function transformDatesToLocale<T extends Record<string, unknown>>(obj: T | null | undefined, locale?: string): T | null {
  if (!obj) return null;
  const transformed: any = { ...(obj as any) };
  try {
    if ('createdAt' in transformed && transformed.createdAt) {
      transformed.createdAt = formatToLocaleDate(transformed.createdAt as any, locale) || transformed.createdAt;
    }
    if ('updatedAt' in transformed && transformed.updatedAt) {
      transformed.updatedAt = formatToLocaleDate(transformed.updatedAt as any, locale) || transformed.updatedAt;
    }
  } catch (e) {
    // noop - preserve original values on error
  }
  return transformed as T;
}

// Transform arrays or paginated responses
export function transformArrayDatesToLocale<T extends Record<string, unknown>>(items: T[] | { content?: T[] } | null | undefined, locale?: string): T[] | { content?: T[] } | null {
  if (!items) return null;
  // Handle paginated shape: { content: [...] }
  if (Array.isArray(items)) {
    return items.map(i => transformDatesToLocale(i, locale) as T);
  }
  if (typeof items === 'object' && 'content' in items && Array.isArray((items as any).content)) {
    const copy: any = { ...(items as any) };
    copy.content = (copy.content || []).map((i: T) => transformDatesToLocale(i, locale) as T);
    return copy;
  }
  return items as any;
}

/**
 * Formats a date for backend compatibility (ISO 8601 with milliseconds and UTC Z suffix)
 * Matches Java: @JsonFormat(pattern = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", timezone = "UTC")
 * Note: Preserves the literal local time values picked by the user.
 */
export function formatToBackendDateTime(dateInput?: string | number | Date | null): string | null {
  if (!dateInput) return null;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput as any);
  if (!isValidDate(date)) return null;

  return date.toISOString();
}

/**
 * Formats a date for backend compatibility (ISO 8601 without timezone suffix)
 * Matches Java: LocalDateTime
 * Note: Preserves the literal local time values picked by the user.
 */
export function formatToLocalDateTime(dateInput?: string | number | Date | null): string | null {
  if (!dateInput) return null;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput as any);
  if (!isValidDate(date)) return null;

  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}

/**
 * Formats a date for backend compatibility (ISO 8601 date-only: yyyy-MM-dd)
 * Preserves the literal local date picked by the user.
 */
export function formatToBackendDate(dateInput?: string | number | Date | null): string | null {
  if (!dateInput) return null;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput as any);
  if (!isValidDate(date)) return null;

  return format(date, "yyyy-MM-dd");
}

// Backwards-compatible Indian-specific helpers
export function formatToIndianDate(dateInput?: string | number | Date | null) {
  return formatToLocaleDate(dateInput, 'en-IN');
}

export function transformDatesToIndian<T extends Record<string, unknown>>(obj: T | null | undefined) {
  return transformDatesToLocale(obj, 'en-IN');
}

export function transformArrayDatesToIndian<T extends Record<string, unknown>>(items: T[] | { content?: T[] } | null | undefined) {
  return transformArrayDatesToLocale(items, 'en-IN');
}

/**
 * Returns the current browser time in the backend-compatible ISO string format (yyyy-MM-ddTHH:mm:ss).
 */
export function getCurrentBrowserTime(): string {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
}

/**
 * Formats a duration in hours (number) to a string like "6Hrs - 30mins"
 */
export function formatDurationHrsMins(totalHours: number | null | undefined): string {
  if (totalHours === null || totalHours === undefined || isNaN(totalHours)) return "—";
  
  const isNegative = totalHours < 0;
  const absHours = Math.abs(totalHours);
  const totalMinutes = Math.round(absHours * 60);
  
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  
  const sign = isNegative ? "-" : "";

  if (mins === 0) {
    return `${sign}${hrs} hrs`;
  }

  return `${sign}${hrs} hrs ${mins} mins`;
}
